'use client';

import React, { useId } from 'react';

import { cn } from '@/lib/utils';

export type SoccerBallSpinnerProps = {
  size?: number;
  label?: string;
  className?: string;
};

const SURFACE_TILE_WIDTH = 60;
const HEX_COLUMN_STEP = 15;
const HEX_ROW_STEP = 17.4;
const SURFACE_TILE_HEIGHT = HEX_ROW_STEP * 4;
const HEX_POINTS = '10,0 5,8.7 -5,8.7 -10,0 -5,-8.7 5,-8.7';
const PENTAGON_POINTS = '0,-9 8.6,-2.8 5.3,7.3 -5.3,7.3 -8.6,-2.8';
const SURFACE_PATTERN_COLUMNS = Array.from({ length: 8 }, (_, index) => index - 2);
const SURFACE_PATTERN_ROWS = Array.from({ length: 10 }, (_, index) => index - 3);
const PANEL_LIGHT_FILL = '#f4fbfc';
const PANEL_LIGHT_STROKE = '#9bc4cb';
const PANEL_DARK_FILL = '#075968';

function positiveModulo(value: number, divisor: number) {
  return ((value % divisor) + divisor) % divisor;
}

function isDarkSurfacePanel(columnIndex: number, rowIndex: number) {
  return positiveModulo(columnIndex, 2) === 0 && positiveModulo(rowIndex, 2) === 0;
}

function SoccerSurfacePanel({
  x,
  y,
  filled,
  reference = false,
}: {
  x: number;
  y: number;
  filled: boolean;
  reference?: boolean;
}) {
  return (
    <g transform={`translate(${x} ${y})`} data-testid={reference ? 'soccer-ball-spinner-surface-panel' : undefined}>
      <polygon
        points={HEX_POINTS}
        fill={PANEL_LIGHT_FILL}
        fillOpacity="0.78"
        stroke={PANEL_LIGHT_STROKE}
        strokeLinejoin="round"
        strokeOpacity="0.74"
        strokeWidth="1.35"
      />
      {filled && (
        <polygon
          points={PENTAGON_POINTS}
          fill={PANEL_DARK_FILL}
          fillOpacity="0.96"
          stroke={PANEL_DARK_FILL}
          strokeLinejoin="round"
          strokeOpacity="0.98"
          strokeWidth="1.35"
          data-testid={reference ? 'soccer-ball-spinner-dark-pentagon' : undefined}
        />
      )}
    </g>
  );
}

function renderPatternPanels(keyPrefix: string, includeReference = false) {
  return SURFACE_PATTERN_COLUMNS.flatMap((columnIndex) =>
    SURFACE_PATTERN_ROWS.map((rowIndex) => {
      const rowOffset = positiveModulo(columnIndex, 2) === 0 ? 0 : HEX_ROW_STEP / 2;

      return (
        <SoccerSurfacePanel
          key={`${keyPrefix}-${columnIndex}-${rowIndex}`}
          x={columnIndex * HEX_COLUMN_STEP}
          y={rowIndex * HEX_ROW_STEP + rowOffset}
          filled={isDarkSurfacePanel(columnIndex, rowIndex)}
          reference={includeReference && columnIndex === 2 && rowIndex === 2}
        />
      );
    })
  );
}

export function SoccerBallSpinner({ size = 32, label = 'Loading', className }: SoccerBallSpinnerProps) {
  const reactId = useId();
  const normalizedId = reactId.replaceAll(':', '');
  const clipPathId = `sportsfi-soccer-ball-spinner-clip-${normalizedId}`;
  const sphereGradientId = `sportsfi-soccer-ball-spinner-sphere-${normalizedId}`;
  const edgeGradientId = `sportsfi-soccer-ball-spinner-edge-${normalizedId}`;
  const highlightGradientId = `sportsfi-soccer-ball-spinner-highlight-${normalizedId}`;
  const surfacePatternId = `sportsfi-soccer-ball-spinner-surface-${normalizedId}`;
  const staticSurfacePatternId = `sportsfi-soccer-ball-spinner-static-surface-${normalizedId}`;

  return (
    // biome-ignore lint/a11y/useSemanticElements: role="status" keeps the decorative SVG spinner announced without changing its inline wrapper semantics.
    <span
      role="status"
      aria-label={label}
      className={cn('sportsfi-soccer-ball-spinner inline-flex items-center justify-center', className)}
      style={{ height: size, width: size }}
    >
      <svg
        aria-hidden="true"
        className="sportsfi-soccer-ball-spinner__svg block size-full overflow-visible"
        focusable="false"
        viewBox="0 0 100 100"
      >
        <defs>
          <clipPath id={clipPathId}>
            <circle cx="50" cy="45" r="38" />
          </clipPath>
          <radialGradient id={sphereGradientId} cx="31%" cy="24%" r="78%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="34%" stopColor="#f7fbfc" />
            <stop offset="68%" stopColor="#d9edf1" />
            <stop offset="100%" stopColor="#7fb6c2" />
          </radialGradient>
          <radialGradient id={edgeGradientId} cx="70%" cy="72%" r="70%">
            <stop offset="0%" stopColor="#041f27" stopOpacity="0" />
            <stop offset="66%" stopColor="#041f27" stopOpacity="0.06" />
            <stop offset="100%" stopColor="#041f27" stopOpacity="0.34" />
          </radialGradient>
          <radialGradient id={highlightGradientId} cx="28%" cy="20%" r="42%">
            <stop offset="0%" stopColor="#ffffff" stopOpacity="0.64" />
            <stop offset="55%" stopColor="#ffffff" stopOpacity="0.18" />
            <stop offset="100%" stopColor="#ffffff" stopOpacity="0" />
          </radialGradient>
          <pattern
            id={surfacePatternId}
            height={SURFACE_TILE_HEIGHT}
            overflow="visible"
            patternUnits="userSpaceOnUse"
            width={SURFACE_TILE_WIDTH}
            x="0"
            y="0"
          >
            <animateTransform
              accumulate="sum"
              additive="sum"
              attributeName="patternTransform"
              calcMode="linear"
              data-testid="soccer-ball-spinner-pattern-motion"
              dur="1.45s"
              from="0 0"
              repeatCount="indefinite"
              to={`-${SURFACE_TILE_WIDTH} 0`}
              type="translate"
            />
            {renderPatternPanels('motion', true)}
          </pattern>
          <pattern
            id={staticSurfacePatternId}
            height={SURFACE_TILE_HEIGHT}
            overflow="visible"
            patternUnits="userSpaceOnUse"
            width={SURFACE_TILE_WIDTH}
            x="0"
            y="0"
          >
            {renderPatternPanels('static')}
          </pattern>
        </defs>

        <ellipse
          className="sportsfi-soccer-ball-spinner__shadow"
          cx="52"
          cy="88"
          rx="27"
          ry="6"
          fill="#123842"
          opacity="0.2"
        />

        <g className="sportsfi-soccer-ball-spinner__ball">
          <g className="sportsfi-soccer-ball-spinner__tilt">
            <circle cx="50" cy="45" r="38" fill={`url(#${sphereGradientId})`} />

            <g clipPath={`url(#${clipPathId})`}>
              <rect
                className="sportsfi-soccer-ball-spinner__pattern sportsfi-soccer-ball-spinner__pattern-motion"
                data-testid="soccer-ball-spinner-pattern"
                fill={`url(#${surfacePatternId})`}
                height="150"
                width="140"
                x="-20"
                y="-30"
              />
              <rect
                className="sportsfi-soccer-ball-spinner__pattern-static"
                fill={`url(#${staticSurfacePatternId})`}
                height="150"
                width="140"
                x="-20"
                y="-30"
              />

              <ellipse cx="36" cy="23" rx="24" ry="15" fill={`url(#${highlightGradientId})`} />
              <circle cx="50" cy="45" r="38" fill={`url(#${edgeGradientId})`} />
              <path d="M16 46C19 66 33 80 53 82" fill="none" stroke="#031f27" strokeOpacity="0.12" strokeWidth="5" />
            </g>

            <circle cx="50" cy="45" r="38" fill="none" stroke="#ffffff" strokeOpacity="0.7" strokeWidth="1.2" />
          </g>
        </g>
      </svg>
      <span className="sr-only">{label}</span>
      <style>{`
        .sportsfi-soccer-ball-spinner__tilt {
          transform-box: fill-box;
          transform-origin: 50% 50%;
          transform: rotate(-24deg) skewY(-5deg) scaleX(0.92) scaleY(1.02);
        }

        .sportsfi-soccer-ball-spinner__ball {
          animation: sportsfi-soccer-ball-spinner-hop 1.45s ease-in-out infinite;
          transform-box: fill-box;
          transform-origin: 50% 50%;
          will-change: transform;
        }

        .sportsfi-soccer-ball-spinner__pattern {
          transform-box: fill-box;
        }

        .sportsfi-soccer-ball-spinner__pattern-static {
          display: none;
        }

        .sportsfi-soccer-ball-spinner__shadow {
          transform-origin: 52px 88px;
          animation: sportsfi-soccer-ball-spinner-shadow 1.45s ease-in-out infinite;
        }

        @keyframes sportsfi-soccer-ball-spinner-hop {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-7%);
          }
        }

        @keyframes sportsfi-soccer-ball-spinner-shadow {
          0%,
          100% {
            opacity: 0.2;
            transform: scaleX(0.96);
          }
          50% {
            opacity: 0.28;
            transform: scaleX(1.04);
          }
        }

        @keyframes sportsfi-soccer-ball-spinner-pulse {
          0%,
          100% {
            opacity: 0.78;
          }
          50% {
            opacity: 1;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .sportsfi-soccer-ball-spinner__pattern-motion,
          .sportsfi-soccer-ball-spinner__ball,
          .sportsfi-soccer-ball-spinner__shadow {
            animation: none;
          }

          .sportsfi-soccer-ball-spinner__pattern-motion {
            display: none;
          }

          .sportsfi-soccer-ball-spinner__pattern-static {
            display: block;
          }

          .sportsfi-soccer-ball-spinner__svg {
            animation: sportsfi-soccer-ball-spinner-pulse 1.8s ease-in-out infinite;
          }
        }
      `}</style>
    </span>
  );
}
